import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EmbeddedStripePaymentElement from '../components/EmbeddedStripePaymentElement';
import { CheckCircle, AlertCircle, X } from 'lucide-react';
import LegalFootnote from './components/LegalFootnote';
import './portal-v2.css';

const fmtUSD = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ClientV2GuestPayment = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => { document.title = 'Pay Invoice | WesTex Legal'; }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/guest/pay/${token}`);
        if (!active) return;
        if (res.ok) {
          const d = await res.json();
          const inv = d.invoice || d;
          setInvoice(inv);
          const isPaid = d.status === 'paid' || (inv && (inv.portal_paid || parseFloat(inv.balance_due || 0) === 0));
          if (isPaid) setPaid(true);
        } else {
          const t = await res.text();
          setError(t || `Could not load invoice (${res.status})`);
        }
      } catch (e) { if (active) setError(e.message); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [API_URL, token]);

  const startPayment = () => { setError(null); setShowPay(true); };

  const createIntent = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/guest/pay-intent/${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ origin_url: window.location.origin }),
    });
    if (!res.ok) {
      const raw = await res.text();
      let msg = raw || 'Could not start payment';
      try {
        const j = JSON.parse(raw);
        if (j && j.detail) msg = typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail);
      } catch (_) { /* not JSON, keep raw text */ }
      throw new Error(msg);
    }
    return await res.json();
  }, [API_URL, token]);

  // Handle 3DS redirect-back: ?redirect_status=succeeded
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('redirect_status') === 'succeeded') setPaid(true);
  }, []);

  const balance = parseFloat(invoice?.balance_due || 0);

  return (
    <div className="portal-v2">
      <div className="pv2-login-shell">
        <div className="pv2-login-card" data-testid="pv2-guest-card" style={{ maxWidth: 540 }}>
          <div className="pv2-mono-tag" style={{ marginBottom: 14 }}>Secure Payment · WesTex Legal</div>
          <h1 className="pv2-display" style={{ fontSize: 36, marginBottom: 8 }}>
            Pay your <span className="pv2-display-italic">invoice.</span>
          </h1>

          {loading && <p style={{ color: 'var(--pv2-text-muted)' }}>Loading invoice…</p>}
          {error && <div className="pv2-alert pv2-alert-red"><AlertCircle size={14} /><span>{error}</span></div>}

          {!loading && invoice && paid && (
            <div className="pv2-alert pv2-alert-green"><CheckCircle size={14} /><span>This invoice has been paid in full. Thank you.</span></div>
          )}

          {!loading && invoice && !paid && (
            <>
              <div style={{ padding: 24, background: 'var(--pv2-ink-soft)', border: '1px solid var(--pv2-ink-line)', marginBottom: 20 }}>
                {/* Invoice # === Job # (locked in: they are always the same) */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span className="pv2-mono-sm">Invoice #</span>
                  <span className="pv2-table-job-num">{invoice.servemanager_job_number || invoice.invoice_number || invoice.id?.slice(0,8)}</span>
                </div>
                {parseFloat(invoice.total_paid || 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, color: 'var(--pv2-green-text)' }}>
                    <span className="pv2-mono-sm">Already Paid</span>
                    <span>−{fmtUSD(invoice.total_paid)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 12, borderTop: '1px solid var(--pv2-ink-line)', fontSize: 18, fontFamily: 'Playfair Display, serif', fontWeight: 700, color: 'var(--pv2-red-bright)' }}>
                  <span>Balance Due</span>
                  <span>{fmtUSD(balance)}</span>
                </div>
              </div>

              <button onClick={startPayment} className="pv2-btn pv2-btn-primary" style={{ width: '100%', padding: '16px' }} data-testid="pv2-guest-pay">
                Pay {fmtUSD(balance)} now
              </button>

              <LegalFootnote action="submitting payment" testId="pv2-guest-legal" />
            </>
          )}
        </div>
      </div>

      {showPay && invoice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(8px)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 32 }}>
          <div style={{ width: '100%', maxWidth: 560 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, color: '#fff' }}>
              <span className="pv2-mono">Complete payment · {fmtUSD(balance)}</span>
              <button onClick={() => setShowPay(false)} style={{ background: 'transparent', border: '1px solid var(--pv2-ink-line)', color: '#fff', padding: 8, cursor: 'pointer' }} data-testid="pv2-guest-pay-close">
                <X size={16} />
              </button>
            </div>
            <div style={{ background: 'var(--pv2-ink)', border: '1px solid var(--pv2-ink-line)' }} data-testid="pv2-guest-pay-modal">
              <EmbeddedStripePaymentElement
                createIntent={createIntent}
                returnUrl={`${window.location.origin}/payment-success`}
                onAlreadyPaid={() => { setShowPay(false); setPaid(true); }}
                onSuccess={(pi) => {
                  setShowPay(false);
                  navigate(`/payment-success?payment_intent_id=${pi?.id || ''}&continue=${encodeURIComponent('/portal/pay/' + token)}`);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientV2GuestPayment;
